import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import Alert, { type AlertColor } from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'

// One queueing snackbar for the whole app, as mik-ng's admin has. Messages
// raised while one is showing wait their turn rather than replacing it --
// "booking cancelled" followed instantly by "option promoted" should show
// both, since the second is news.
interface SnackMessage {
  text: string
  severity: AlertColor
}

interface SnackbarApi {
  showSnackbar: (text: string, options?: { severity?: AlertColor }) => void
}

const SnackbarContext = createContext<SnackbarApi | null>(null)

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<SnackMessage | null>(null)
  const queue = useRef<SnackMessage[]>([])

  const showNext = useCallback(() => {
    const next = queue.current.shift()
    setCurrent(next ?? null)
  }, [])

  const showSnackbar = useCallback<SnackbarApi['showSnackbar']>(
    (text, options) => {
      const message: SnackMessage = { text, severity: options?.severity ?? 'success' }
      setCurrent((showing) => {
        if (showing) {
          queue.current.push(message)
          return showing
        }
        return message
      })
    },
    [],
  )

  const value = useMemo(() => ({ showSnackbar }), [showSnackbar])

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={current !== null}
        autoHideDuration={4000}
        onClose={(_, reason) => {
          // Not on clickaway: on a phone, a stray tap while reading the
          // message should not dismiss it.
          if (reason !== 'clickaway') showNext()
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // Clear of the bottom navigation on small screens.
        sx={{ mb: { xs: 8, md: 0 } }}
      >
        {current ? (
          <Alert severity={current.severity} variant="filled" onClose={showNext} sx={{ width: '100%' }}>
            {current.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </SnackbarContext.Provider>
  )
}

export function useSnackbar(): SnackbarApi {
  const ctx = useContext(SnackbarContext)
  if (!ctx) throw new Error('useSnackbar must be used inside a SnackbarProvider')
  return ctx
}
